import {
  ExtractFnReturnType,
  MutationConfig,
  queryClient,
  QueryConfig,
} from "@/lib/react-query";
import {
  RequestAcceptGcu,
  RequestUpdateUser,
  SupportedLanguageEnum,
  UserInfo,
  UserInfoSchema,
} from "@/types/user";
import { client } from "@/utils/axios.config";
import i18next from "@/utils/i18n";
import { useMutation, useQuery } from "@tanstack/react-query";
import { HttpStatusCode } from "axios";
import { NavigateFunction } from "react-router-dom";
import { toast } from "react-toastify";

//GetUser
export const getUserInfo = async (userId: string): Promise<UserInfo> => {
  return UserInfoSchema.parse(await client.get(`/api/user/${userId}`));
};

type QueryFnTypeGetUserInfo = typeof getUserInfo;

type UseUserInfoOptions = {
  userId: string;
  config?: QueryConfig<QueryFnTypeGetUserInfo>;
};

export const useUserInfo = ({ userId, config = {} }: UseUserInfoOptions) => {
  return useQuery<ExtractFnReturnType<QueryFnTypeGetUserInfo>>({
    ...config,
    staleTime: 60 * 60 * 1000, //not refetching for the next hour
    queryKey: ["user"],
    queryFn: () => getUserInfo(userId),
  });
};

//UpdateUser
export const updateUser = async ({
  userId,
  firstName,
  lastName,
  language,
}: {
  userId: string;
  firstName: string;
  lastName: string;
  language: SupportedLanguageEnum;
}): Promise<UserInfo> => {
  const request: RequestUpdateUser = {
    lastName: lastName,
    firstName: firstName,
    language: language,
  };
  return UserInfoSchema.parse(await client.put(`/api/user/${userId}`, request));
};

type UseUpdateUserOptions = {
  config?: MutationConfig<typeof updateUser>;
};

export const useUpdateUser = ({ config }: UseUpdateUserOptions = {}) => {
  return useMutation({
    onError: (apiError) => {
      if (HttpStatusCode.NotFound === apiError?.response?.status) {
        toast.error(i18next.t("toast.user.update.error.notFound"), {
          toastId: "useUpdateProject.NotFound",
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
    ...config,
    mutationFn: updateUser,
  });
};

//accept CGU
export const acceptCgu = async ({
  userId,
  cguVersion,
}: {
  userId: string;
  cguVersion: string;
}): Promise<UserInfo> => {
  const request: RequestAcceptGcu = {
    cguVersion: cguVersion,
  };
  return UserInfoSchema.parse(
    await client.put(`/api/user/${userId}/cgu`, request)
  );
};

type UseAcceptCguOptions = {
  config?: MutationConfig<typeof acceptCgu>;
};

export const useAcceptCgu = ({ config }: UseAcceptCguOptions = {}) => {
  return useMutation({
    onError: (apiError) => {
      if (HttpStatusCode.NotFound === apiError?.response?.status) {
        toast.error(i18next.t("toast.user.acceptCgu.error.notFound"), {
          toastId: "useAcceptCgu.NotFound",
        });
      }
      if (HttpStatusCode.Conflict === apiError?.response?.status) {
        toast.error(i18next.t("toast.user.acceptCgu.error.conflict"), {
          toastId: "useAcceptCgu.Conflict",
        });
      }
    },
    onSuccess: () => {
      //TODO: optimistic update
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
    ...config,
    mutationFn: acceptCgu,
  });
};

//Delete user
export const deleteUser = async ({
  userId,
}: {
  userId: string;
}): Promise<void> => {
  return await client.delete(`/api/user/${userId}`);
};

type UseDeleteUserOptions = {
  config?: MutationConfig<typeof deleteUser>;
  navigate: NavigateFunction;
};

export const useDeleteUser = ({ config, navigate }: UseDeleteUserOptions) => {
  return useMutation({
    onError: (apiError) => {
      if (HttpStatusCode.NotFound === apiError?.response?.status) {
        toast.error(i18next.t("toast.user.update.error.notFound"), {
          toastId: "useDeleteUser.NotFound",
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
      navigate("/logout");
    },
    ...config,
    mutationFn: deleteUser,
  });
};
